import { useState, useEffect, useCallback } from "react";
import { useSocketEmitter } from "./use-sockets";
import { useAuth } from "./use-auth";

const STORAGE_KEY = "astrologer-online-status";

export const useOnlineStatus = () => {
  const { emitter } = useSocketEmitter();
  const { astrologer } = useAuth();
  
  // Initialize from localStorage or default to false
  const [isOnline, setIsOnline] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Save to localStorage whenever status changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(isOnline));
    } catch (error) {
      console.warn("Failed to save online status to localStorage:", error);
    }
  }, [isOnline]);

  // Emit status to server whenever it changes
  useEffect(() => {
    if (astrologer?.astrologerId) {
      emitter("status-update", { isActive: isOnline });
      console.log(`[OnlineStatus] Status emitted: ${isOnline}`);
    }
  }, [isOnline, astrologer?.astrologerId, emitter]);

  // Note: Removed visibility change handler as it was causing issues
  // when switching between tabs during calls. Status is already persisted
  // via localStorage and emitted on explicit toggle or login.

  // Initialize status on login
  useEffect(() => {
    if (astrologer?.astrologerId && !document.hidden) {
      // Emit current status when astrologer logs in
      emitter("status-update", { isActive: isOnline });
      console.log(`[OnlineStatus] Initial status emitted for astrologer: ${isOnline}`);
    }
  }, [astrologer?.astrologerId]);

  const toggleOnlineStatus = useCallback(() => {
    setIsOnline(prev => {
      const newStatus = !prev;
      console.log(`[OnlineStatus] Toggling to: ${newStatus ? "Online" : "Offline"}`);
      return newStatus;
    });
  }, []);

  const setOnlineStatus = useCallback((status) => {
    setIsOnline(status);
    console.log(`[OnlineStatus] Setting status to: ${status ? "Online" : "Offline"}`);
  }, []);

  return {
    isOnline,
    toggleOnlineStatus,
    setOnlineStatus,
  };
};
